import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { BellRing, BellOff, Mail, Loader2, ChevronRight } from 'lucide-react';

const KIND = {
  owner: { label: 'Inhaber · E-Sign', cls: 'border-teal-500/40 bg-teal-500/10 text-teal-300' },
  broker: { label: 'Broker · E-Sign', cls: 'border-violet-500/40 bg-violet-500/10 text-violet-300' },
  request: { label: 'Broker · NDA angefordert', cls: 'border-sky-500/40 bg-sky-500/10 text-sky-300' },
};

export const NdaReminderWidget = () => {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(null);

  const load = useCallback(() => {
    api.getNdaRemindersDue().then(r => setItems(r.data.items || [])).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  if (items.length === 0) return null;

  const remind = async (it) => {
    setBusy(it.id);
    try {
      if (it.kind === 'esign') await api.remindNda(it.id); else await api.remindProposalNda(it.id);
      toast.success(`Erinnerung an ${it.recipient_name || it.recipient_email} gesendet`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Fehlgeschlagen'); }
    setBusy(null);
  };

  const dismiss = async (it) => {
    setBusy(it.id);
    try {
      if (it.kind === 'esign') await api.dismissNdaReminder(it.id); else await api.dismissProposalNdaReminder(it.id);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Fehlgeschlagen'); }
    setBusy(null);
  };

  return (
    <div className="bg-card border border-amber-500/30 rounded-sm" data-testid="nda-reminder-widget">
      <div className="px-4 pt-3 pb-1 flex items-center gap-2">
        <BellRing className="w-4 h-4 text-amber-400" />
        <p className="font-heading text-base text-foreground">NDA-Erinnerungen fällig</p>
        <span className="text-[10px] font-mono border border-amber-500/40 bg-amber-500/10 text-amber-300 rounded px-1.5 py-0.5" data-testid="nda-reminder-count">{items.length}</span>
        <span className="text-[10px] text-muted-foreground hidden sm:inline">— seit 3+ Tagen ohne Unterschrift bzw. Rücklauf. Ein Klick schickt genau eine freundliche Erinnerung.</span>
      </div>
      <div className="px-2 pb-2 space-y-1">
        {items.map(it => {
          const k = KIND[it.kind === 'broker' ? 'request' : it.variant === 'broker' ? 'broker' : 'owner'];
          const canRemind = it.kind === 'broker' || isAdmin;
          return (
            <div key={`${it.kind}-${it.id}`} className="flex items-center gap-2 px-2 py-1.5 rounded-sm hover:bg-secondary/40 text-xs flex-wrap"
              data-testid={`nda-reminder-item-${it.id}`}>
              <span className={`text-[9px] font-mono border rounded px-1 py-0.5 uppercase ${k.cls}`}>{k.label}</span>
              <button onClick={() => navigate(it.url)} className="text-foreground/90 hover:text-teal-300 inline-flex items-center gap-0.5 font-medium"
                data-testid={`nda-reminder-open-${it.id}`}>
                {it.project_name || it.company || it.recipient_name} <ChevronRight className="w-3 h-3" />
              </button>
              <span className="text-muted-foreground truncate">{it.recipient_name}{it.recipient_email ? ` · ${it.recipient_email}` : ''}</span>
              <span className="text-[10px] font-mono text-amber-300">seit {it.days} Tg{it.opened ? ' · geöffnet' : ''}</span>
              {it.user_name && <span className="text-[10px] text-muted-foreground hidden md:inline">von {it.user_name}</span>}
              {canRemind && (
                <span className="ml-auto inline-flex items-center gap-1">
                  <button onClick={() => remind(it)} disabled={busy === it.id} data-testid={`nda-reminder-send-${it.id}`}
                    className="inline-flex items-center gap-1 border border-amber-500/50 bg-amber-500/10 text-amber-200 hover:bg-amber-500/20 rounded-sm px-2 py-0.5 text-[11px] transition-colors">
                    {busy === it.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Mail className="w-3 h-3" />} Erinnern
                  </button>
                  <button onClick={() => dismiss(it)} disabled={busy === it.id} title="Nicht erinnern" data-testid={`nda-reminder-dismiss-${it.id}`}
                    className="text-muted-foreground hover:text-foreground p-0.5"><BellOff className="w-3.5 h-3.5" /></button>
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
